/**
 * Sentry Error Monitoring Integration
 *
 * CRITICAL: This file handles PII sanitization for government compliance.
 * All error data is sanitized before being sent to Sentry cloud.
 *
 * PII Types Sanitized:
 * - NIB numbers (National ID)
 * - Bank account numbers
 * - Email addresses
 * - Phone numbers
 * - Personal names
 * - Addresses
 */

import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import type { Router } from "vue-router";

/**
 * PII Detection Patterns
 * These patterns identify sensitive data that must be sanitized
 */
const PII_PATTERNS = {
  // NIB number format: 12345678 (8 digits)
  nibNumber: /\b\d{8}\b/g,

  // Bank account patterns (various formats)
  bankAccount: /\b\d{10,16}\b/g,

  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,

  // Phone numbers (Bahamas format: +1-242-XXX-XXXX or local)
  phone: /(\+?1[-.]?)?(\()?242([\).-])?[\d\-. ]{7,}/g,

  // Credit card patterns (just in case)
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
};

/**
 * Sensitive field names that should be redacted
 */
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'authorization',
  'api_key',
  'apiKey',
  'secret',
  'nib_number',
  'nibNumber',
  'bank_account',
  'bankAccount',
  'routing_number',
  'routingNumber',
  'social_security',
  'ssn',
];

/**
 * Sanitize a string by replacing PII with redacted placeholders
 */
function sanitizeString(str: string): string {
  if (typeof str !== 'string') return str;

  let sanitized = str;

  // Replace NIB numbers
  sanitized = sanitized.replace(PII_PATTERNS.nibNumber, '[NIB-REDACTED]');

  // Replace bank accounts
  sanitized = sanitized.replace(PII_PATTERNS.bankAccount, '[ACCOUNT-REDACTED]');

  // Replace emails
  sanitized = sanitized.replace(PII_PATTERNS.email, '[EMAIL-REDACTED]');

  // Replace phones
  sanitized = sanitized.replace(PII_PATTERNS.phone, '[PHONE-REDACTED]');

  // Replace credit cards
  sanitized = sanitized.replace(PII_PATTERNS.creditCard, '[CARD-REDACTED]');

  return sanitized;
}

/**
 * Recursively sanitize an object, removing PII from all nested values
 */
function sanitizeObject(obj: any, depth = 0): any {
  // Prevent infinite recursion
  if (depth > 10) return '[MAX-DEPTH]';

  if (obj === null || obj === undefined) return obj;

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, depth + 1));
  }

  // Handle objects
  if (typeof obj === 'object') {
    const sanitized: any = {};

    for (const [key, value] of Object.entries(obj)) {
      // Check if field name is sensitive
      const lowerKey = key.toLowerCase();
      const isSensitiveField = SENSITIVE_FIELDS.some(field =>
        lowerKey.includes(field.toLowerCase())
      );

      if (isSensitiveField) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'string') {
        sanitized[key] = sanitizeString(value);
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeObject(value, depth + 1);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  // Handle strings
  if (typeof obj === 'string') {
    return sanitizeString(obj);
  }

  // Primitives (numbers, booleans, etc.)
  return obj;
}

/**
 * Sanitize Sentry breadcrumbs (user actions, API calls, console logs)
 */
function sanitizeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  // Sanitize breadcrumb message
  if (breadcrumb.message) {
    breadcrumb.message = sanitizeString(breadcrumb.message);
  }

  // Sanitize breadcrumb data
  if (breadcrumb.data) {
    breadcrumb.data = sanitizeObject(breadcrumb.data);
  }

  // Sanitize URLs in navigation breadcrumbs
  if (breadcrumb.category === 'navigation' && breadcrumb.data?.to) {
    breadcrumb.data.to = sanitizeString(breadcrumb.data.to);
  }

  // Sanitize console logs
  if (breadcrumb.category === 'console') {
    breadcrumb.message = sanitizeString(breadcrumb.message || '');
  }

  return breadcrumb;
}

/**
 * Initialize Sentry with PII sanitization
 */
export function initializeSentry(app: App, router: Router): void {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_APP_ENV || 'development';

  // Only initialize if DSN is configured
  if (!sentryDsn) {
    console.warn('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    app,
    dsn: sentryDsn,
    // Never attach IP address / cookies / auth headers at the SDK envelope
    // level — those are added BEFORE beforeSend runs, bypassing the
    // sanitization below. Citizen IP addresses are PII.
    sendDefaultPii: false,
    environment,

    // Integration configuration
    integrations: [
      // Vue Router integration for navigation tracking
      Sentry.browserTracingIntegration({ router }),

      // Replay integration for session replay (debugging)
      Sentry.replayIntegration({
        maskAllText: true, // Mask all text by default (PII protection)
        blockAllMedia: true, // Block images/videos (may contain PII)
      }),
    ],

    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in staging

    // Session replay sample rate
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always capture replay when error occurs

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // PII Sanitization - CRITICAL
    beforeSend(event, hint) {
      // Sanitize error message
      if (event.message) {
        event.message = sanitizeString(event.message);
      }

      // Sanitize exception values
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map(exception => ({
          ...exception,
          value: exception.value ? sanitizeString(exception.value) : exception.value,
        }));
      }

      // Sanitize stack traces
      if (event.exception?.values) {
        event.exception.values.forEach(exception => {
          if (exception.stacktrace?.frames) {
            exception.stacktrace.frames = exception.stacktrace.frames.map(frame => ({
              ...frame,
              vars: frame.vars ? sanitizeObject(frame.vars) : frame.vars,
            }));
          }
        });
      }

      // Sanitize request data
      if (event.request) {
        if (event.request.data) {
          event.request.data = sanitizeObject(event.request.data);
        }
        if (event.request.headers) {
          event.request.headers = sanitizeObject(event.request.headers);
        }
        if (event.request.url) {
          event.request.url = sanitizeString(event.request.url);
        }
      }

      // Sanitize extra context
      if (event.extra) {
        event.extra = sanitizeObject(event.extra);
      }

      // Sanitize user context (no PII in user identification)
      if (event.user) {
        event.user = {
          id: event.user.id ? 'user-' + event.user.id.toString().substring(0, 8) : undefined,
          // DO NOT send: email, username, ip_address
        };
      }

      return event;
    },

    // Sanitize breadcrumbs
    beforeBreadcrumb(breadcrumb, hint) {
      return sanitizeBreadcrumb(breadcrumb);
    },

    // Ignore known non-critical errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',

      // Network errors (already handled by our error handler)
      'Network Error',
      'NetworkError',
    ],
  });

  console.info(`[Sentry] Initialized for ${environment} environment`);
}

/**
 * Capture error to Sentry (used by errorHandler.ts)
 */
export function captureSentryError(error: unknown, context?: Record<string, any>): void {
  // Add sanitized context
  if (context) {
    Sentry.setContext('errorContext', sanitizeObject(context));
  }

  // Capture error
  if (error instanceof Error) {
    Sentry.captureException(error);
  } else {
    Sentry.captureMessage(String(error));
  }
}

/**
 * Set user context (called on login) - NO PII
 */
export function setSentryUser(userId: string | number): void {
  Sentry.setUser({
    id: 'user-' + userId.toString().substring(0, 8), // Anonymized user ID
    // DO NOT set email, username, or other PII
  });
}

/**
 * Clear user context (called on logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}
